import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import theme, { rem } from '../../utils/theme'
import Flag from '../Flag'
import StackedNavLink, { StyledStackedNavLink } from './StackedNavLink'

export const StyledStackedNavItem = styled.li.withConfig({
  shouldForwardProp: prop => !['hasChildItems', 'childItemsOpen', 'customComponent'].includes(prop),
})`
  display: block;
  position: relative;
  border-radius: calc(var(--nav-item-height) / 2);
  transition: ${theme.mainNav.transition};

  li + & {
    margin-top: var(--nav-item-spacing);
  }

  ${props =>
    props.hasChildItems &&
    css`
      ${
        props.childItemsOpen &&
        css`
        background-color: ${`var(--gameday-newDropdown-buttonBgColorProminent, ${theme.newDropdown.buttonBgColorProminent})`};
      `
      }
    `}
`

const StyledChildItemsWrap = styled.div.withConfig({
  shouldForwardProp: prop => !['childItemsOpen', 'isCollapsed', 'numChildItems'].includes(prop),
})`
  height: 0;
  overflow: hidden;
  margin: 0 0 var(--nav-list-spacing);
  transition: ${theme.mainNav.transition};

  ${props =>
    props.childItemsOpen &&
    css`
      height: calc(var(--nav-item-child-height) * ${props.numChildItems} + var(--nav-list-spacing));
    `}

  ${props =>
    props.isCollapsed &&
    css`
      height: 0;
      opacity: 0;
    `}
`

const StyledChildItems = styled.ul.withConfig({
  shouldForwardProp: prop => !['childItemsOpen'].includes(prop),
})`
  display: block;
  list-style: none;
  padding: 0 var(--nav-list-spacing) var(--nav-list-spacing);
  transition: ${theme.mainNav.transition};
  opacity: 0;

  ${props =>
    props.childItemsOpen &&
    css`
      opacity: 1;
    `}

  ${StyledStackedNavLink} {
    & > a,
    & > button {
      height: var(--nav-item-child-height);

      &:hover {
        background-color: ${`var(--gameday-newDropdown-buttonBgColor, ${theme.newDropdown.buttonBgColor})`};
      }
    }
  }
`

const StyledChildItem = styled.li`
  display: block;
`

const StyledChildItemsToggle = styled.button.withConfig({
  shouldForwardProp: prop => !['childItemsOpen', 'isCollapsed'].includes(prop),
})`
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  position: absolute;
  top: ${rem(3)};
  right: ${rem(3)};
  width: calc(var(--nav-item-height) - ${rem(6)});
  height: calc(var(--nav-item-height) - ${rem(6)});
  padding: 0;
  border-radius: 100%;
  cursor: pointer;

  > svg {
    transition: ${theme.transitions.short};

    ${props =>
      props.childItemsOpen &&
      css`
        transform: rotate(180deg);
      `}
  }

  &:hover {
    background-color: ${`var(--gameday-newDropdown-buttonBgColorProminentHover, ${theme.newDropdown.buttonBgColorProminentHover})`};
  }

  &:focus {
    outline: 0;
    box-shadow: ${theme.focusRings.shadow({ inset: true })};
  }
`

function StackedNavItem(props) {
  const { icon, label, childItems, openItemWithActiveChild = true, tabIndex = 0, customComponent, isCollapsed } = props
  const [childItemsOpen, setChildItemsOpen] = useState(false)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''

  useEffect(() => {
    const startOpen = openItemWithActiveChild && childItems && childItems.filter(c => c.isActive).length > 0
    if (startOpen) {
      setChildItemsOpen(true)
    } else {
      setChildItemsOpen(false)
    }
  }, [pathname])

  const hasChildItems = childItems && childItems.length > 0

  const childWithAlert = (childItems || []).find(i => !!i.alert)
  const parentAlert = !childItemsOpen && childWithAlert ? childWithAlert.alert : null

  return (
    <StyledStackedNavItem
      hasChildItems={hasChildItems}
      childItemsOpen={childItemsOpen}
      customComponent={customComponent}
      data-testid={`${label} parent`}
    >
      <StackedNavLink {...props} isParent alert={parentAlert} />
      {hasChildItems && (
        <>
          <StyledChildItemsToggle
            childItemsOpen={childItemsOpen}
            onClick={() => setChildItemsOpen(!childItemsOpen)}
            aria-label={`${childItemsOpen ? 'Close' : 'Open'} ${label} sub-menu`}
            tabIndex={tabIndex}
            isCollapsed={isCollapsed}
            data-testid={`${label} sub-menu`}
          >
            {!isCollapsed && icon && icon !== 'arrow' && (
              <Flag
                variant={icon}
                title={icon}
                style={{
                  position: 'absolute',
                  top: rem(7),
                  right: rem(30),
                }}
              />
            )}
            {!isCollapsed && <ArrowDropDownIcon style={{ fontSize: 18 }} />}
          </StyledChildItemsToggle>
          <StyledChildItemsWrap
            childItemsOpen={childItemsOpen}
            numChildItems={childItems.filter(i => i.show).length}
            isCollapsed={isCollapsed}
          >
            <StyledChildItems childItemsOpen={childItemsOpen}>
              {childItems.map((item, i) => {
                const { show = true } = item
                if (!show) return null
                return (
                  <StyledChildItem
                    data-testid={`${item.label}`}
                    key={`${item.label}.${
                      // biome-ignore lint/suspicious/noArrayIndexKey: TODO: pre-existing, address separately
                      i
                    }`}
                  >
                    <StackedNavLink
                      isChild
                      {...item}
                      // eslint-disable-next-line no-nested-ternary
                      tabIndex={!childItemsOpen ? -1 : item.tabIndex ? item.tabIndex : 0}
                    />
                  </StyledChildItem>
                )
              })}
            </StyledChildItems>
          </StyledChildItemsWrap>
        </>
      )}
      {!isCollapsed && !hasChildItems && icon && icon !== 'arrow' && icon !== 'new' && (
        <Flag
          variant={icon}
          title={icon}
          style={{
            position: 'absolute',
            top: rem(9),
            right: rem(10),
          }}
        />
      )}
    </StyledStackedNavItem>
  )
}

export default StackedNavItem
